import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  fetchNotificationSettings,
  NotificationSettings,
  SmsAllowance,
  UpdateNotificationSettingsPayload,
  updateNotificationSettings,
} from "../api/notifications";

const EVENT_ROWS: Array<{
  label: string;
  smsField: keyof NotificationSettings;
  emailField: keyof NotificationSettings;
}> = [
  {
    label: "Confirmation",
    smsField: "sms_confirmation_enabled",
    emailField: "email_confirmation_enabled",
  },
  {
    label: "Reminder",
    smsField: "sms_reminder_enabled",
    emailField: "email_reminder_enabled",
  },
  {
    label: "Cancellation",
    smsField: "sms_cancellation_enabled",
    emailField: "email_cancellation_enabled",
  },
  {
    label: "Waitlist availability",
    smsField: "sms_waitlist_availability_enabled",
    emailField: "email_waitlist_availability_enabled",
  },
];

const allowanceLabel: Record<SmsAllowance["status"], string> = {
  available: "Available",
  exhausted: "Exhausted",
  unlimited: "Unlimited",
  not_included: "Not included",
  unavailable: "Unavailable",
};

function describeAllowance(allowance: SmsAllowance): string {
  if (allowance.status === "unlimited") return "Unlimited SMS allowance";
  if (allowance.status === "available") {
    if (allowance.remaining == null) return "SMS is available";
    return `${allowance.remaining} SMS remaining`;
  }
  return allowance.message ?? allowanceLabel[allowance.status];
}

function buildChangedSettingsPayload(
  draft: NotificationSettings,
  saved: NotificationSettings
): UpdateNotificationSettingsPayload {
  return (Object.keys(draft) as Array<keyof NotificationSettings>).reduce(
    (payload, field) => {
      if (draft[field] !== saved[field]) {
        return { ...payload, [field]: draft[field] };
      }
      return payload;
    },
    {} as UpdateNotificationSettingsPayload
  );
}

const NotificationSettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["notificationSettings"],
    queryFn: fetchNotificationSettings,
    staleTime: 60_000,
  });
  const [draft, setDraft] = useState<NotificationSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.settings) {
      setDraft(data.settings);
      setError(null);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notificationSettings"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    },
  });

  const isDirty = useMemo(() => {
    if (!draft || !data?.settings) return false;
    return JSON.stringify(draft) !== JSON.stringify(data.settings);
  }, [data?.settings, draft]);

  const update = <K extends keyof NotificationSettings>(
    field: K,
    value: NotificationSettings[K]
  ) => {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleNumberChange = (
    field: "reminder_primary_hours" | "reminder_secondary_hours",
    rawValue: string
  ) => {
    if (rawValue === "") {
      update(field, null as NotificationSettings[typeof field]);
      return;
    }

    const parsed = Number(rawValue);
    if (Number.isInteger(parsed)) {
      update(field, parsed as NotificationSettings[typeof field]);
    }
  };

  const handleSave = () => {
    if (!draft || !data?.settings) return;
    setError(null);
    mutation.mutate(buildChangedSettingsPayload(draft, data.settings));
  };

  if (isLoading || !draft || !data) {
    return <Box p={3}>Loading notification settings...</Box>;
  }

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load notification settings.</Alert>
      </Box>
    );
  }

  const allowance = data.sms_allowance;

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
        Notifications
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <Typography variant="h6">SMS Settings</Typography>
              <Chip
                size="small"
                label={allowanceLabel[allowance.status]}
                color={allowance.allowed ? "success" : "default"}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={draft.sms_enabled}
                  onChange={(e) => update("sms_enabled", e.target.checked)}
                />
              }
              label="SMS Enabled"
            />

            <Typography variant="body2" color="text.secondary">
              {describeAllowance(allowance)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Booking Notifications
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}></Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2">SMS</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2">Email</Typography>
            </Grid>

            {EVENT_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <Grid item xs={4}>
                  {row.label}
                </Grid>
                <Grid item xs={4}>
                  <Switch
                    checked={Boolean(draft[row.smsField])}
                    onChange={(e) => update(row.smsField, e.target.checked)}
                    inputProps={{ "aria-label": `${row.label} SMS` }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Switch
                    checked={Boolean(draft[row.emailField])}
                    onChange={(e) => update(row.emailField, e.target.checked)}
                    inputProps={{ "aria-label": `${row.label} email` }}
                  />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Reminder Timing
          </Typography>

          <Stack spacing={2}>
            <TextField
              type="number"
              label="Primary Reminder (hours before)"
              value={draft.reminder_primary_hours ?? ""}
              onChange={(e) =>
                handleNumberChange("reminder_primary_hours", e.target.value)
              }
              fullWidth
              inputProps={{ min: 1, max: 168, step: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={draft.reminder_secondary_enabled}
                  onChange={(e) =>
                    update("reminder_secondary_enabled", e.target.checked)
                  }
                />
              }
              label="Enable second reminder"
            />

            {draft.reminder_secondary_enabled && (
              <TextField
                type="number"
                label="Secondary Reminder (hours before)"
                value={draft.reminder_secondary_hours ?? ""}
                onChange={(e) =>
                  handleNumberChange("reminder_secondary_hours", e.target.value)
                }
                fullWidth
                inputProps={{ min: 1, max: 72, step: 1 }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!isDirty || mutation.isPending}
      >
        {mutation.isPending ? "Saving..." : "Save Changes"}
      </Button>
    </Box>
  );
};

export default NotificationSettingsPage;
